import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import API from './services/api';
import { clearMaintenanceDelegateToken } from './utils/maintenanceAccess';
import { getAppPath, getStorageItem, removeStorageItems, setStorageItem } from './utils/runtimeMode';

const AuthContext = createContext();

const excludedAllowedPages = new Set(['/hazard-forms', '/dashboard', '/employee-dashboard']);

const filterAllowedPages = (pages = []) => pages.filter((page) => !excludedAllowedPages.has(page));

const readAllowedPages = () => {
  try {
    return filterAllowedPages(JSON.parse(sessionStorage.getItem('allowedPages') || '[]'));
  } catch (error) {
    return [];
  }
};

const writeAllowedPages = (pages = []) => {
  try {
    sessionStorage.setItem('allowedPages', JSON.stringify(pages));
  } catch (error) {
    // Ignore sessionStorage failures.
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = getStorageItem('user');
    if (savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch (e) {
        console.error('Error parsing saved user:', e);
        return null;
      }
    }
    return null;
  });

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return getStorageItem('isAuthenticated') === 'true';
  });

  const [allowedPages, setAllowedPages] = useState(readAllowedPages);

  const [loading, setLoading] = useState(true);

  const loadAllowedPages = useCallback(async () => {
    try {
      const response = await API.get('/api/permissions');
      const pages = filterAllowedPages(Array.isArray(response.data?.pages) ? response.data.pages : []);
      setAllowedPages(pages);
      writeAllowedPages(pages);
      return pages;
    } catch (error) {
      const cachedPages = readAllowedPages();
      setAllowedPages(cachedPages);
      return cachedPages;
    }
  }, []);

  // Set up axios interceptor to include token with every request
  useEffect(() => {
    const token = getStorageItem('authToken');
    if (token) {
      API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    return () => {
      delete API.defaults.headers.common['Authorization'];
    };
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const token = getStorageItem('authToken');
      
      console.log('🔍 AuthContext checkAuth starting...', {
        hasToken: !!token,
        apiBaseURL: API.defaults.baseURL,
        userAgent: navigator.userAgent,
        hostname: window.location.hostname
      });
      
      if (token) {
        // CRITICAL FIX: Always set default headers first
        API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        console.log('📝 Checking auth with token:', token.substring(0,10) + '...');
        
        try {
          // FIXED: Use the endpoint that actually exists
          const response = await API.get('/api/auth/me', { 
            withCredentials: true 
            // Don't set headers here - use defaults
          });
          
          console.log('🔍 Auth check response:', response.status, response.data);
          
          if (response.data && (response.data.isAuthenticated || response.data.success)) {
            setUser(response.data.user);
            setIsAuthenticated(true);
            setStorageItem('user', JSON.stringify(response.data.user));
            setStorageItem('isAuthenticated', 'true');
            await loadAllowedPages();
            console.log('✅ Authentication verified with server');
          } else {
            console.log('❌ Server says not authenticated');
            handleLogout(false);
          }
        } catch (error) {
          console.error('Error checking auth with server:', error);
          console.error('Error details:', {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data,
            url: error.config?.url
          });
          
          // Continue using cached credentials on server error
          const savedUser = getStorageItem('user');
          if (savedUser) {
            try {
              const parsedUser = JSON.parse(savedUser);
              setUser(parsedUser);
              setIsAuthenticated(true);
              await loadAllowedPages();
              console.log('Using cached credentials due to server error');
            } catch (e) {
              handleLogout(false);
            }
          } else {
            handleLogout(false);
          }
        }
      } else {
        console.log('❌ No token found in localStorage');
        handleLogout(false);
      }
    } finally {
      setLoading(false);
    }
  }, [loadAllowedPages]);

  // Call checkAuth on component mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]); // Now checkAuth is memoized so this is safe

  // Helper to avoid code duplication
  const handleLogout = (redirect = true) => {
    setUser(null);
    setIsAuthenticated(false);
    setAllowedPages([]);
    removeStorageItems(['user', 'isAuthenticated', 'authToken', 'userEmail', 'userRole']);
    clearMaintenanceDelegateToken();
    writeAllowedPages([]);
    
    if (redirect) {
      // Use the current origin for the redirect instead of hardcoded localhost
      window.location.href = `${window.location.origin}${getAppPath('/login')}`;
    }
  };

  const login = async (credentials) => {
    try {
      const response = await API.post('/api/login', {
        username: credentials.email,
        password: credentials.password,
      }, { withCredentials: true });

      if (response.data.success) {
        console.log('Login successful:', response.data);

        // Create a user object from the response
        const userObject = {
          username: response.data.username,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          role: response.data.role,
          jobTitle: response.data.jobTitle || response.data.TITLE || response.data.title || '',
          departmentId: response.data.departmentId ?? response.data.DEPARTMENT_ID ?? response.data.department_id ?? null,
          department: response.data.department || response.data.DEPARTMENT || '',
          departmentIds: response.data.departmentIds || response.data.DEPARTMENT_IDS || [],
          departments: response.data.departments || response.data.DEPARTMENTS || [],
          jobTitles: response.data.jobTitles || response.data.TITLES || [],
          hasFireDepartmentAccess: !!(response.data.hasFireDepartmentAccess ?? response.data.HAS_FIRE_DEPARTMENT_ACCESS),
          hasNonFireDepartmentAccess: !!(response.data.hasNonFireDepartmentAccess ?? response.data.HAS_NON_FIRE_DEPARTMENT_ACCESS)
        };

        // Store everything in localStorage
        setStorageItem('authToken', response.data.token);
        setStorageItem('user', JSON.stringify(userObject));
        setStorageItem('isAuthenticated', 'true');
        setStorageItem('userEmail', response.data.username);
        setStorageItem('userRole', response.data.role);

        // CRITICAL FIX: Set the Authorization header immediately
        API.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        console.log('✅ Authorization header set:', `Bearer ${response.data.token.substring(0,10)}...`);

        // Update context state
        setUser(userObject);
        setIsAuthenticated(true);
        await loadAllowedPages();

        console.log('Auth state updated and token stored');
        return true;
      } else {
        console.error('Login failed:', response.data.message || response.data.error || response.data);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error.response?.data?.message || error.response?.data?.error || error.message);
      return false;
    }
  };

  const logout = async () => {
    try {
      await API.post('/logout', {}, { withCredentials: true });
      handleLogout(true); // With redirect
    } catch (error) {
      console.error('Error during logout:', error);
      handleLogout(true); // With redirect anyway
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, logout, allowedPages }}>
      {loading ? <div>Loading authentication...</div> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
